// "View as" (impersonation) - the same GSWebAPIv2 endpoints swiftMarketing uses (connectGS3.0 src/api/impersonation.js),
// so who may view as whom, and the audit trail, are identical in both apps.
//
// Targets and Start always act for the real signed-in user ({ skipActAs: true } keeps helpers/impersonation.js from
// adding X-GSD-Act-As). Stop and Me send the header: Stop audits the person in it, Me answers as that person.
//
// Every call resolves with the API envelope's `data` and throws a plain Error carrying the API's own message.
import axios from 'axios'
import auth from '@/authConfig.js'

const API_ENDPOINT = process.env.VUE_APP_API_ENDPOINT
const BASE = 'api/GSDesignImpersonation'

async function authHeaders() {
  const tokenResponse = await auth.acquireTokenResponse()
  const token = tokenResponse?.accessToken
  return token ? { Authorization: 'Bearer ' + token } : {}
}

function failure(err) {
  const body = err?.response?.data
  const msg = body?.message || body?.Message || (Array.isArray(body?.errors) && body.errors[0]) || err?.message || 'Please try again'
  const e = new Error(msg)
  e.status = err?.response?.status
  e.cause = err
  return e
}

function unwrap(body) {
  if (body && typeof body === 'object' && ('isSuccess' in body || 'issuccess' in body)) {
    const ok = body.isSuccess ?? body.issuccess
    if (ok === false) throw new Error(body.message || 'Please try again')
    return body.data
  }
  return body
}

async function request(method, path, { data, params, skipActAs = false } = {}) {
  try {
    const res = await axios({ method, url: `${API_ENDPOINT}${path}`, data, params, headers: await authHeaders(), skipActAs })
    return unwrap(res.data)
  } catch (err) {
    throw err?.response ? failure(err) : err
  }
}

export default {
  /**
   * People the real user may view as, one group (level) per page.
   * → { groups: [{ level, label, count }], items: [{ target, kind, azure_id, name, title, email, orgid, division, role_label, level }], total, page, page_size }
   */
  targets({ search, level, page = 1, pageSize = 25 } = {}) {
    return request('get', `${BASE}/Targets`, { params: { search: search || undefined, level: level || undefined, page, pageSize }, skipActAs: true })
  },

  /** Validates + audits impersonation.start → the target's profile, with an `impersonation` block for the banner. */
  start(target) {
    return request('post', `${BASE}/Start`, { data: { target }, skipActAs: true })
  },

  /** Audits impersonation.stop for the person in X-GSD-Act-As (sent one last time). */
  stop() {
    return request('post', `${BASE}/Stop`)
  },

  /** GSDesignMe/Get: the person being viewed as (re-checked server-side, 403 when no longer allowed), else the real user. */
  me() {
    return request('get', 'api/GSDesignMe/Get')
  }
}
